import React from 'react';
import { Search, MapPin, Filter, Loader2, RefreshCw } from 'lucide-react';
import { useApp } from '../context/AppContext';
import ProviderCard from './ProviderCard';
import ProviderSkeleton from './ProviderSkeleton';
import FiltersPanel from './FiltersPanel';

const SearchView = () => {
  const {
    searchQuery,
    locationQuery,
    showFilters,
    setShowFilters,
    loading,
    error,
    providers,
    pagination,
    handleSearchQueryChange,
    handleLocationQueryChange,
    executeSearch
  } = useApp();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50">
      <div className="bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-500 pt-8 pb-32 px-4">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-3">Find Trusted Home Health Care</h2>
          <p className="text-teal-100 text-lg mb-8">Connect with verified professionals for personalized care at home</p>
          <div className="bg-white rounded-2xl shadow-2xl p-2 flex flex-col md:flex-row gap-2">
            <div className="flex-1 flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl">
              <Search className="w-5 h-5 text-slate-400" />
              <input 
                type="text" 
                placeholder="Search by service, specialty..." 
                className="flex-1 bg-transparent text-slate-800 outline-none" 
                value={searchQuery} 
                onChange={e => handleSearchQueryChange(e.target.value)} 
              />
            </div>
            <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl md:w-64">
              <MapPin className="w-5 h-5 text-slate-400" />
              <input 
                type="text" 
                placeholder="Location" 
                className="flex-1 bg-transparent text-slate-800 outline-none" 
                value={locationQuery} 
                onChange={e => handleLocationQueryChange(e.target.value)} 
              />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold ${showFilters ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-700'}`}>
              <Filter className="w-5 h-5" />Filters
            </button>
          </div>
        </div>
      </div>
      
      {showFilters && <FiltersPanel />}
      
      <div className={`max-w-7xl mx-auto px-4 ${showFilters ? '' : '-mt-20'} pb-12`}>
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between">
            <span className="text-red-600">{error}</span>
            <button onClick={executeSearch} className="flex items-center gap-2 text-red-600 hover:text-red-700">
              <RefreshCw className="w-4 h-4" /> Retry
            </button>
          </div>
        )}
        
        <div className="flex justify-between items-center mb-6">
          <p className="text-slate-600">
            {loading ? (
              <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Searching...</span>
            ) : (
              <><span className="font-semibold text-slate-800">{pagination.total}</span> professionals found</>
            )}
          </p>
        </div>
        
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => <ProviderSkeleton key={i} />)}
          </div>
        ) : providers.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-500 text-lg">No providers found matching your criteria.</p>
            <p className="text-slate-400 mt-2">Try adjusting your filters or search terms.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {providers.map(p => <ProviderCard key={p.id} provider={p} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchView;
